import React from 'react';
import moment from 'moment';
import { Grid, makeStyles, Typography, withTheme } from '@material-ui/core';
import { CardItem, CardContainer } from '../utilities/commonUtilities/CardItem';
import { ReactComponent as LabImage } from '../../resources/images/lab-badge.svg';
import { ReactComponent as DoctorReviewIcon } from '../../resources/images/doctor-review.svg';
import ProgressBar from '../ProgressBar';
import { computeProgressPercent } from '../utilities/commonUtilities/TaskCard';
import { ReactComponent as NoteIcon } from '../../resources/images/note.svg';
import NextUp from '../utilities/commonUtilities/NextUpComponent';
import { AuditEventAction, createClientAnalytics } from '../../utils/ClientAnalytics';
import { useDispatch } from 'react-redux';
import { sendClientAnalytics } from '../../actions';
import { PATH_LABS } from '../../containers/RoutePaths';
import { WAITING_REVIEW_NEXT_UP_CARD_TEXT } from '../../resources/messages.json';
import styled from 'styled-components';
import { styleBreakDesktop } from '../utilities/layouts/Globals';

const useStyles = makeStyles(theme => ({
    progressCard: {
        marginTop: theme.spacing(12),
        [theme.breakpoints.down(theme.breakpoints.values['lg'])]: {
            marginTop: theme.spacing(11.5),
        },
    },
    gridItem: {
        marginRight: theme.spacing(1),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
    },
    dateContainer: {
        marginTop: theme.spacing(1),
    },
    endText: {
        textAlign: 'right',
        justifyContent: 'flex-end',
        fontSize: '14px',
    },
    doctorReviewTitle: {
        fontSize: theme.spacing(2),
        lineHeight: 1,
    },
    progressBar: {
        height: theme.spacing(3),
    },
    taskHeader: {
        marginTop: theme.spacing(5),
        marginBottom: theme.spacing(1),
        [styleBreakDesktop(theme)]: {
            marginTop: theme.spacing(6),
            marginBottom: theme.spacing(2),
        },
    },
}));

const StyledDoctorReviewIcon = withTheme(styled(DoctorReviewIcon)`
    ${({ theme }) => `
    background-color: ${theme.palette.background.default};
    border-radius: 50%;
    transform: translateX(-3px);
    `}
`);

const ProgressTaskCard = ({ submittedDateObj, expectedRespDateObj }) => {
    const classes = useStyles();
    const progressPercent = computeProgressPercent(submittedDateObj, expectedRespDateObj);

    return (
        <CardContainer className={classes.progressCard}>
            <Grid container wrap="nowrap">
                <Grid item xs zeroMinWidth className={classes.gridItem}>
                    <Typography variant="h2" className={classes.doctorReviewTitle}>
                        Doctor's Review
                    </Typography>
                    <Typography>Waiting for your doctor</Typography>
                </Grid>
                <Grid item>
                    <StyledDoctorReviewIcon />
                </Grid>
            </Grid>
            <ProgressBar className={classes.progressBar} widthPercentage={progressPercent} />
            <Grid container wrap="nowrap" alignItems="center" className={classes.dateContainer}>
                <Grid item xs zeroMinWidth>
                    <Typography variant="h4">Submitted</Typography>
                    <Typography variant="caption">{submittedDateObj.format('MMMM D')}</Typography>
                </Grid>
                <Grid item>
                    <Typography variant="h4">Expected response</Typography>
                    <Typography className={classes.endText}>{expectedRespDateObj.format('MMMM D')}</Typography>
                </Grid>
            </Grid>
        </CardContainer>
    );
};

export const WaitingReviewTaskBoard = ({ submittedDate, expectedResponseDate, isPendingClinicianManualLabsRetrieval, YourJourneyCard, hasViewableLabResults = false }) => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const expectedRespDateObj = moment(expectedResponseDate, moment.ISO_8601);
    const submittedDateObj = moment(submittedDate, moment.ISO_8601);
    const labCardTitle = 'View your lab results';
    const labCardBody = 'Tap to view';

    const nextUpCardTitle = "Doctor's Note";
    const nextUpCardText = WAITING_REVIEW_NEXT_UP_CARD_TEXT;

    const nextUpCardImage = NoteIcon;

    const onCardClick = () => {
        const labViewedAnalytics = createClientAnalytics(AuditEventAction.LAB_VIEWED.category, AuditEventAction.LAB_VIEWED.action);
        dispatch(sendClientAnalytics(labViewedAnalytics));
        window.location = PATH_LABS;
    };

    return (
        <>
            <ProgressTaskCard submittedDateObj={submittedDateObj} expectedRespDateObj={expectedRespDateObj} />
            {!isPendingClinicianManualLabsRetrieval && hasViewableLabResults && (
                <>
                    <Typography data-cy="label_task_title" variant="h2" className={classes.taskHeader}>
                        Your Tasks
                    </Typography>
                    <CardItem Badge={LabImage} onClick={onCardClick} title={labCardTitle} subtitle={labCardBody} datatestid="viewLabResultsCard" />
                </>
            )}
            <NextUp cardTitle={nextUpCardTitle} cardText={nextUpCardText} CardImage={nextUpCardImage} />
            {YourJourneyCard}
        </>
    );
};
